/**
 * Build stamp for the web footer — pure formatter + the env reader.
 *
 * `ci-web.yml` stamps three vars into the build (2026-10-08):
 *
 *   - `VITE_APP_VERSION`    `vX.Y` (or `vX.Y.Z`), on `main` only: the first
 *                           `^vX.Y` of the release merge commit's body, which
 *                           is the release PR title ("v0.91 - …"). Empty when
 *                           it does not match, and on every other branch.
 *   - `VITE_APP_BUILD_DATE` the UTC build date, `YYYY-MM-DD`. ci-web builds
 *                           and deploys in one run, so on prod it is the
 *                           deploy day; a re-run stamps the re-run's date.
 *   - `VITE_APP_COMMIT`     the short sha.
 *
 * ci-web runs on EVERY push to `main` (no path filter there), so the version
 * on prod is always the release that is live, even for a release that
 * touched no web file.
 *
 * A local build sets none of them, and that is what makes it `local`: with
 * no stamp at all the footer reads `local` whatever stage the build resolved
 * (a laptop without `VITE_APP_STAGE` resolves to `dev`). Otherwise each part
 * is optional: a missing or malformed one is dropped from the string instead
 * of shown as a blank.
 */
import { type Stage, resolveStage } from "@nu-assist/shared-fe";

export interface BuildInfo {
  stage: Stage;
  version?: string;
  buildDate?: string;
  commit?: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `2026-10-09` → `Oct 9, 2026`. Parsed by hand, not through `Date`, so the
 *  viewer's time zone can never move a UTC date by a day. */
export function formatBuildDate(iso: string | undefined): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  if (!m) return undefined;
  const month = MONTHS[Number(m[2]) - 1];
  const day = Number(m[3]);
  if (!month || day < 1 || day > 31) return undefined;
  return `${month} ${day}, ${m[1]}`;
}

/**
 * The footer string:
 *
 *   - prod  `v0.92 · deployed Oct 9, 2026`, or `deployed Oct 9, 2026 · abc1234`
 *           when the release title carried no version
 *   - dev   `dev · built Oct 9, 2026 · abc1234`
 *   - local `local`, and so is any build with no stamp at all
 */
export function formatFooterVersion(info: BuildInfo): string {
  const stamped = Boolean(info.version || info.buildDate || info.commit);
  if (info.stage === "local" || !stamped) return "local";
  const version = /^v\d+\.\d+(\.\d+)?$/.test(info.version ?? "") ? info.version : undefined;
  const date = formatBuildDate(info.buildDate);
  const commit = info.commit?.trim() || undefined;
  const parts =
    info.stage === "prod"
      ? version
        ? [version, date && `deployed ${date}`]
        : [date && `deployed ${date}`, commit]
      : ["dev", date && `built ${date}`, commit];
  const text = parts.filter(Boolean).join(" · ");
  return text || info.stage;
}

export function readBuildInfo(): BuildInfo {
  return {
    stage: resolveStage(),
    version: import.meta.env.VITE_APP_VERSION,
    buildDate: import.meta.env.VITE_APP_BUILD_DATE,
    commit: import.meta.env.VITE_APP_COMMIT,
  };
}
